import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { extractNextUrlPath } from '../utils/api';
import { BASE_URL } from '../config/api';
import ServiceCard from '../components/ServiceCard';
import SplineBackground from '../components/SplineBackground';
import { getHomeSplineSceneUrl } from '../config/spline';
import SEOHead from '../components/SEOHead';
import './Home.css';

const SWIPE_THRESHOLD = 50; /* pixels - high enough to not block taps on service links */
const HOME_REQUEST_CONFIG = { _skipGlobalLoader: true };

const Home = () => {
  const { i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [featuredWorks, setFeaturedWorks] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [currentFeaturedWorkIndex, setCurrentFeaturedWorkIndex] = useState(0);
  const [currentFeaturedBlogIndex, setCurrentFeaturedBlogIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const servicePointerStartXRef = React.useRef(null);
  const serviceDidDragRef = React.useRef(false);
  const featuredPointerStartXRef = React.useRef(null);
  const featuredDidDragRef = React.useRef(false);

  useEffect(() => {
    fetchServices();
    fetchFeaturedWorks();
    fetchFeaturedBlogs();
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Reset slider position when window resizes to prevent showing partial cards
      setCurrentServiceIndex(0);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate card width based on screen size
  const getCardWidth = () => {
    if (windowWidth <= 480) return windowWidth - 40; // Wider cards on mobile, accounting for padding
    if (windowWidth <= 768) return 280; // card width only
    return 350; // desktop card width only
  };

  // Calculate gap between cards
  const getCardGap = () => {
    return windowWidth <= 480 ? 0 : 30; // No gap on mobile for strict width matching
  };

  // Calculate how many cards fit in the visible area
  const getVisibleCardsCount = (itemsCount, includeSliderPadding = true) => {
    const cardWidth = getCardWidth();
    const gap = getCardGap();
    // Match CSS padding: desktop 100px*2, tablet 60px*2, mobile 0 (no padding for strict clipping)
    const basePadding = windowWidth <= 480 ? 0 : windowWidth <= 768 ? 120 : windowWidth <= 1024 ? 160 : 200;
    // Account for slider inner padding - responsive: desktop 70px, tablet 60px, mobile 0 (no padding)
    let sliderPadding = 0;
    if (includeSliderPadding && itemsCount > 0) {
      if (windowWidth <= 480) {
        sliderPadding = 0; // No padding on mobile for strict clipping
      } else if (windowWidth <= 768) {
        sliderPadding = 100; // 50px each side
      } else if (windowWidth <= 1024) {
        sliderPadding = 120; // 60px each side
      } else {
        sliderPadding = 140; // 70px each side
      }
    }
    const availableWidth = windowWidth - basePadding - sliderPadding;
    
    // Calculate how many cards fit: (availableWidth + gap) / (cardWidth + gap)
    // Use Math.floor to ensure we only count full cards
    // On mobile, use larger buffer (10px) to ensure we never show partial cards
    const buffer = windowWidth <= 480 ? 10 : 5;
    const cardsThatFit = Math.floor((availableWidth - buffer + gap) / (cardWidth + gap));
    return Math.max(1, cardsThatFit); // at least 1 card
  };

  // Calculate total width for one card including gap
  const getCardStepWidth = () => {
    return getCardWidth() + getCardGap();
  };

  const fetchServices = async () => {
    try {
      // Fetch all pages if paginated
      let allServices = [];
      let nextUrl = '/services/';
      
      while (nextUrl) {
        const response = await api.get(nextUrl, HOME_REQUEST_CONFIG);
        
        const services = response.data.results || response.data || [];
        allServices = [...allServices, ...services];
        
        if (response.data.next) {
          nextUrl = extractNextUrlPath(response.data.next);
        } else {
          nextUrl = null;
        }
      }
      
      setServices(allServices); // Load all services for slider
      setCurrentServiceIndex(0); // Reset slider position when services change
    } catch (err) {
    }
  };

  const fetchFeaturedWorks = async () => {
    try {
      let allWork = [];
      let nextUrl = '/content/our-work/';

      while (nextUrl) {
        const response = await api.get(nextUrl, HOME_REQUEST_CONFIG);
        const items = response.data.results || response.data || [];
        allWork = [...allWork, ...items];

        if (response.data.next) {
          nextUrl = extractNextUrlPath(response.data.next);
        } else {
          nextUrl = null;
        }
      }

      const featured = allWork.filter((item) => Boolean(item.is_featured));
      setFeaturedWorks(featured);
    } catch (err) {
    }
  };

  const fetchFeaturedBlogs = async () => {
    try {
      let allBlogs = [];
      let nextUrl = '/content/blogs/';

      while (nextUrl) {
        const response = await api.get(nextUrl, HOME_REQUEST_CONFIG);
        const items = response.data.results || response.data || [];
        allBlogs = [...allBlogs, ...items];

        if (response.data.next) {
          nextUrl = extractNextUrlPath(response.data.next);
        } else {
          nextUrl = null;
        }
      }

      const featured = allBlogs.filter((item) => Boolean(item.is_featured));
      setFeaturedBlogs(featured);
    } catch (err) {
    }
  };

  // Check if slider is needed (more cards than can fit)
  const servicesCardsWithoutSlider = getVisibleCardsCount(services.length, false);
  const servicesNeedsSlider = services.length > servicesCardsWithoutSlider;
  const servicesVisibleCardsCount = servicesNeedsSlider ? getVisibleCardsCount(services.length, true) : servicesCardsWithoutSlider;

  const featuredCardsWithoutSlider = getVisibleCardsCount(featuredWorks.length, false);
  const featuredNeedsSlider = featuredWorks.length > featuredCardsWithoutSlider;
  const featuredVisibleCardsCount = featuredNeedsSlider
    ? getVisibleCardsCount(featuredWorks.length, true)
    : featuredCardsWithoutSlider;

  const nextService = () => {
    const maxIndex = Math.max(0, services.length - servicesVisibleCardsCount);
    if (currentServiceIndex < maxIndex) {
      setCurrentServiceIndex(prev => {
        const newIndex = prev + 1;
        return Math.min(newIndex, maxIndex);
      });
    }
  };

  const prevService = () => {
    if (currentServiceIndex > 0) {
      setCurrentServiceIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const handleSliderPointerDown = (e, needsSlider, pointerStartRef, didDragRef) => {
    if (!needsSlider) return;
    pointerStartRef.current = e.clientX;
    didDragRef.current = false;
    /* Don't use setPointerCapture - it retargets the click to the slider div and prevents Link navigation */
  };

  const handleSliderPointerMove = (e, needsSlider, pointerStartRef) => {
    if (!needsSlider || pointerStartRef.current === null) return;
    const deltaX = Math.abs(pointerStartRef.current - e.clientX);
    if (deltaX > SWIPE_THRESHOLD) e.preventDefault();
  };

  const handleSliderPointerUp = (e, needsSlider, pointerStartRef, didDragRef, onNext, onPrev) => {
    if (!needsSlider || pointerStartRef.current === null) return;
    const deltaX = pointerStartRef.current - e.clientX;
    if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
      didDragRef.current = true;
      if (deltaX > 0) onNext();
      else onPrev();
    }
    pointerStartRef.current = null;
  };

  const handleServicesPointerDown = (e) => handleSliderPointerDown(e, servicesNeedsSlider, servicePointerStartXRef, serviceDidDragRef);
  const handleServicesPointerMove = (e) => handleSliderPointerMove(e, servicesNeedsSlider, servicePointerStartXRef);
  const handleServicesPointerUp = (e) => handleSliderPointerUp(e, servicesNeedsSlider, servicePointerStartXRef, serviceDidDragRef, nextService, prevService);

  const servicesSliderRef = React.useRef(null);
  const featuredSliderRef = React.useRef(null);

  // Prevent link navigation when user swiped (not clicked)
  useEffect(() => {
    if (!servicesNeedsSlider && !featuredNeedsSlider) return;
    const handleGlobalClick = (e) => {
      if (serviceDidDragRef.current && e.target.closest('.services-slider')) {
        e.preventDefault();
        e.stopPropagation();
        serviceDidDragRef.current = false;
      } else if (featuredDidDragRef.current && e.target.closest('.featured-work-home-card')) {
        e.preventDefault();
        e.stopPropagation();
        featuredDidDragRef.current = false;
      } else {
        serviceDidDragRef.current = false;
        featuredDidDragRef.current = false;
      }
    };
    window.addEventListener('click', handleGlobalClick, true);
    return () => window.removeEventListener('click', handleGlobalClick, true);
  }, [servicesNeedsSlider, featuredNeedsSlider]);

  // Touchpad horizontal scroll (wheel event) - debounce so one swipe = one step
  const servicesWheelLastTimeRef = React.useRef(0);
  const featuredWheelLastTimeRef = React.useRef(0);

  const handleSliderWheel = (e, needsSlider, wheelLastTimeRef, onPrev, onNext) => {
    if (!needsSlider || Math.abs(e.deltaX) < 10) return;
    e.preventDefault();
    const now = Date.now();
    if (now - wheelLastTimeRef.current < 400) return; // debounce 400ms
    wheelLastTimeRef.current = now;
    if (e.deltaX > 0) onPrev();
    else onNext();
  };

  useEffect(() => {
    const el = servicesSliderRef.current;
    if (!el || !servicesNeedsSlider) return;
    const handler = (e) => handleSliderWheel(e, servicesNeedsSlider, servicesWheelLastTimeRef, prevService, nextService);
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesNeedsSlider, services.length, servicesVisibleCardsCount]);

  useEffect(() => {
    const el = featuredSliderRef.current;
    if (!el || !featuredNeedsSlider) return;
    const handler = (e) => handleSliderWheel(e, featuredNeedsSlider, featuredWheelLastTimeRef, prevFeaturedWork, nextFeaturedWork);
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredNeedsSlider, featuredWorks.length, featuredVisibleCardsCount]);
  
  // Ensure currentServiceIndex doesn't exceed max to prevent showing partial cards
  useEffect(() => {
    if (servicesNeedsSlider && services.length > 0) {
      const maxIndex = Math.max(0, services.length - servicesVisibleCardsCount);
      if (currentServiceIndex > maxIndex) {
        setCurrentServiceIndex(maxIndex);
      }
    } else if (!servicesNeedsSlider) {
      setCurrentServiceIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesNeedsSlider, services.length, servicesVisibleCardsCount]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath.startsWith('/') ? '' : '/media/'}${imagePath}`;
  };

  const localizedFeaturedWorks = useMemo(() => {
    const isKa = String(i18n.language || '').toLowerCase().startsWith('ka');
    return featuredWorks.map((it) => ({
      ...it,
      title: (isKa ? (it.title_ka || it.title_en) : (it.title_en || it.title_ka)) || '',
      short: (isKa ? (it.short_description_ka || it.short_description_en) : (it.short_description_en || it.short_description_ka)) || '',
      categoryTitle: (isKa
        ? (it.category?.title_ka || it.category?.title_en)
        : (it.category?.title_en || it.category?.title_ka)) || '',
      imageUrl: getImageUrl(it.card_photo),
    }));
  }, [featuredWorks, i18n.language]);

  const localizedFeaturedBlogs = useMemo(() => {
    const isKa = String(i18n.language || '').toLowerCase().startsWith('ka');
    return featuredBlogs.map((it) => ({
      ...it,
      title: (isKa ? (it.title_ka || it.title_en) : (it.title_en || it.title_ka)) || '',
      short:
        (isKa ? (it.short_description_ka || it.short_description_en) : (it.short_description_en || it.short_description_ka)) || '',
      imageUrl: getImageUrl(it.main_photo),
      dateLabel: (() => {
        if (!it.date) return '';
        const d = new Date(it.date);
        if (Number.isNaN(d.getTime())) return '';
        if (isKa) {
          const kaMonths = [
            'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
            'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი',
          ];
          return `${kaMonths[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        }
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      })(),
    }));
  }, [featuredBlogs, i18n.language]);

  useEffect(() => {
    if (localizedFeaturedBlogs.length <= 1) return undefined;
    const timer = window.setTimeout(() => {
      setCurrentFeaturedBlogIndex((prev) => (prev + 1) % localizedFeaturedBlogs.length);
    }, 5500);
    return () => window.clearTimeout(timer);
  }, [localizedFeaturedBlogs.length, currentFeaturedBlogIndex]);

  useEffect(() => {
    if (!localizedFeaturedWorks.length) {
      setCurrentFeaturedWorkIndex(0);
      return;
    }
    const maxIndex = Math.max(0, localizedFeaturedWorks.length - featuredVisibleCardsCount);
    if (currentFeaturedWorkIndex > maxIndex) {
      setCurrentFeaturedWorkIndex(maxIndex);
    }
  }, [localizedFeaturedWorks.length, currentFeaturedWorkIndex, featuredVisibleCardsCount]);

  useEffect(() => {
    if (!localizedFeaturedBlogs.length) {
      setCurrentFeaturedBlogIndex(0);
      return;
    }
    if (currentFeaturedBlogIndex >= localizedFeaturedBlogs.length) {
      setCurrentFeaturedBlogIndex(0);
    }
  }, [localizedFeaturedBlogs.length, currentFeaturedBlogIndex]);

  const nextFeaturedWork = () => {
    const maxIndex = Math.max(0, localizedFeaturedWorks.length - featuredVisibleCardsCount);
    if (currentFeaturedWorkIndex < maxIndex) {
      setCurrentFeaturedWorkIndex((prev) => Math.min(prev + 1, maxIndex));
    }
  };

  const prevFeaturedWork = () => {
    if (currentFeaturedWorkIndex > 0) {
      setCurrentFeaturedWorkIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const nextFeaturedBlog = () => {
    if (!localizedFeaturedBlogs.length) return;
    setCurrentFeaturedBlogIndex((prev) => (prev + 1) % localizedFeaturedBlogs.length);
  };

  const prevFeaturedBlog = () => {
    if (!localizedFeaturedBlogs.length) return;
    setCurrentFeaturedBlogIndex((prev) => (prev - 1 + localizedFeaturedBlogs.length) % localizedFeaturedBlogs.length);
  };

  const currentFeaturedBlog = localizedFeaturedBlogs[currentFeaturedBlogIndex] || null;

  const featuredNeedsSwipe = featuredNeedsSlider;
  const handleFeaturedPointerDown = (e) =>
    handleSliderPointerDown(e, featuredNeedsSwipe, featuredPointerStartXRef, featuredDidDragRef);
  const handleFeaturedPointerMove = (e) => handleSliderPointerMove(e, featuredNeedsSwipe, featuredPointerStartXRef);
  const handleFeaturedPointerUp = (e) =>
    handleSliderPointerUp(e, featuredNeedsSwipe, featuredPointerStartXRef, featuredDidDragRef, nextFeaturedWork, prevFeaturedWork);
  const handleFeaturedPointerCancel = (e) =>
    handleSliderPointerUp(e, featuredNeedsSwipe, featuredPointerStartXRef, featuredDidDragRef, nextFeaturedWork, prevFeaturedWork);
  return (
    <div className="home-page">
      <SEOHead
        title="GFM Studio | Live Production & Broadcasting"
        description="GFM Studio is a live production and broadcasting company delivering event streaming, video production, and creative services."
        path="/"
      />
      <section className="landing-section">
        <SplineBackground
          embedded
          sceneUrl={getHomeSplineSceneUrl(i18n.language)}
          className="spline-embedded--landing-bg"
        />
        <div className="container hero-content">
          <h1 className="visually-hidden">GFM Studio | Live Production & Broadcasting</h1>
          <p className={`hero-intro${i18n.language === 'ka' ? ' hero-intro--ka' : ''}`}>
            {i18n.language === 'ka' ? 'თქვენი პარტნიორი ციფრულ სამყაროში' : 'Your Partner in Digital World'}
          </p>
          <div className="hero-logo">
            <img src="/GFM_White.png" alt="GFM Studio" className="hero-logo-img" />
          </div>
          <div className="hero-actions">
            <Link to="/about" className="button button-primary button-large">
              {i18n.language === 'ka' ? 'კომპანია' : 'Company'}
            </Link>
            <Link to="/team" className="button button-primary button-large">
              {i18n.language === 'ka' ? 'ჩვენი გუნდი' : 'Our Team'}
            </Link>
            <Link to="/contact" className="button button-primary button-large hero-contact-button">
              {i18n.language === 'ka' ? 'კონტაქტი' : 'Contact'}
            </Link>
          </div>
        </div>
      </section>

      <section className="services-section">
        <h2>{i18n.language === 'ka' ? 'ჩვენი სერვისები' : 'Our Services'}</h2>
        
        {services.length > 0 ? (
          <div className={`services-slider-container ${servicesNeedsSlider ? 'has-slider' : ''}`}>
            {servicesNeedsSlider && (
            <button
              type="button"
              className="services-slider-btn prev"
              onClick={prevService}
              disabled={currentServiceIndex === 0}
              aria-label={i18n.language === 'ka' ? 'წინა სერვისი' : 'Previous service'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            )}

            <div 
              ref={servicesSliderRef}
              className="services-slider"
              onPointerDown={handleServicesPointerDown}
              onPointerMove={handleServicesPointerMove}
              onPointerUp={handleServicesPointerUp}
              onPointerCancel={handleServicesPointerUp}
            >
              <div className="services-slider-wrapper">
              <div 
                className="services-slider-track"
                style={{
                    transform: servicesNeedsSlider ? `translateX(-${currentServiceIndex * getCardStepWidth()}px)` : 'translateX(0)',
                    justifyContent: servicesNeedsSlider ? 'flex-start' : 'center'
                }}
              >
                {services.map(service => {
                  // Modify service object to include full image URL
                  const serviceWithFullImage = {
                    ...service,
                    main_photo: service.main_photo ? 
                      (service.main_photo.startsWith('http') ? 
                        service.main_photo : 
                        `${BASE_URL}${service.main_photo.startsWith('/') ? '' : '/media/'}${service.main_photo}`
                      ) : null
                  };

                  return <ServiceCard key={service.id} service={serviceWithFullImage} />;
                })}
                </div>
              </div>
            </div>

            {servicesNeedsSlider && (
            <button
              type="button"
              className="services-slider-btn next"
              onClick={nextService}
                disabled={currentServiceIndex >= Math.max(0, services.length - servicesVisibleCardsCount)}
              aria-label={i18n.language === 'ka' ? 'შემდეგი სერვისი' : 'Next service'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
            )}
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#666' }}>
            {i18n.language === 'ka' ? 'სერვისები მალე დაემატება' : 'Services coming soon'}
          </p>
        )}
        
        {services.length > 0 && (
          <div className="services-section-actions">
            <Link
              to="/services"
              className="button button-primary button-large featured-work-view-all-btn"
            >
              {i18n.language === 'ka' ? 'ყველა სერვისის ნახვა' : 'View All Services'}
            </Link>
          </div>
        )}
      </section>

      <section className="featured-work-home-section">
        <h2>{i18n.language === 'ka' ? 'ჩვენი ნამუშევრები' : 'Our Work'}</h2>

        {localizedFeaturedWorks.length > 0 ? (
          <div>
            <div className={`featured-work-slider-container ${featuredNeedsSlider ? 'has-slider' : ''}`}>
              {featuredNeedsSlider && (
                <button
                  type="button"
                  className="featured-work-nav prev"
                  onClick={prevFeaturedWork}
                  disabled={currentFeaturedWorkIndex === 0}
                  aria-label="Previous featured work"
                >
                  ‹
                </button>
              )}
              <div
                ref={featuredSliderRef}
                className="featured-work-stage"
                onPointerDown={handleFeaturedPointerDown}
                onPointerMove={handleFeaturedPointerMove}
                onPointerUp={handleFeaturedPointerUp}
                onPointerCancel={handleFeaturedPointerCancel}
              >
                <div className="featured-work-slider-wrapper">
                  <div
                    className="featured-work-slider-track"
                    style={{
                      transform: featuredNeedsSlider ? `translateX(-${currentFeaturedWorkIndex * getCardStepWidth()}px)` : 'translateX(0)',
                      justifyContent: featuredNeedsSlider ? 'flex-start' : 'center',
                    }}
                  >
                    {localizedFeaturedWorks.map((work) => (
                      <Link key={work.id} to={`/history/our-work/${work.slug}`} className="featured-work-home-card">
                        <div className="featured-work-home-card-media">
                          {work.imageUrl ? (
                            <img src={work.imageUrl} alt={work.title} loading="lazy" />
                          ) : (
                            <div className="featured-work-home-card-media-placeholder" />
                          )}
                        </div>

                        <div className="featured-work-home-card-body">
                          <h3 className="featured-work-home-card-title">{work.title}</h3>
                          {work.short ? <p className="featured-work-home-card-desc">{work.short}</p> : null}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              {featuredNeedsSlider && (
                <button
                  type="button"
                  className="featured-work-nav next"
                  onClick={nextFeaturedWork}
                  disabled={currentFeaturedWorkIndex >= Math.max(0, localizedFeaturedWorks.length - featuredVisibleCardsCount)}
                  aria-label="Next featured work"
                >
                  ›
                </button>
              )}
            </div>

            <div className="featured-work-view-all">
              <Link to="/history/our-work" className="button button-primary button-large featured-work-view-all-btn">
                {i18n.language === 'ka' ? 'ყველა ნამუშევრის ნახვა' : 'View all work'}
              </Link>
            </div>
          </div>
        ) : (
          <p className="featured-work-empty">
            {i18n.language === 'ka' ? 'რჩეული ნამუშევრები მალე დაემატება' : 'Featured work coming soon'}
          </p>
        )}
      </section>

      <section className="featured-blogs-home-section">
        <h2>{i18n.language === 'ka' ? 'ბლოგები' : 'Blogs'}</h2>

        {currentFeaturedBlog ? (
          <div className="featured-blogs-stage">
            <Link to={`/history/blogs/${currentFeaturedBlog.slug}`} className="featured-blogs-hero-card">
              <div className="featured-blogs-layout">
                <div className="featured-blogs-hero-media">
                  {currentFeaturedBlog.imageUrl ? (
                    <img src={currentFeaturedBlog.imageUrl} alt={currentFeaturedBlog.title} loading="lazy" />
                  ) : (
                    <div className="featured-blogs-hero-placeholder" />
                  )}
                  <div className="featured-blogs-hero-overlay" />
                </div>

                <div className="featured-blogs-hero-content">
                  {currentFeaturedBlog.dateLabel ? (
                    <div className="featured-blogs-date">{currentFeaturedBlog.dateLabel}</div>
                  ) : null}
                  <h3>{currentFeaturedBlog.title}</h3>
                  {currentFeaturedBlog.short ? <p>{currentFeaturedBlog.short}</p> : null}
                  <span className="featured-blogs-read">{i18n.language === 'ka' ? 'ვრცლად' : 'Read article'} <span aria-hidden="true">→</span></span>
                </div>
              </div>
            </Link>

            {localizedFeaturedBlogs.length > 1 ? (
              <>
                <button
                  type="button"
                  className="featured-blogs-nav prev"
                  onClick={prevFeaturedBlog}
                  aria-label="Previous featured blog"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="featured-blogs-nav next"
                  onClick={nextFeaturedBlog}
                  aria-label="Next featured blog"
                >
                  ›
                </button>

                <div className="featured-blogs-dots">
                  {localizedFeaturedBlogs.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`featured-blogs-dot ${idx === currentFeaturedBlogIndex ? 'active' : ''}`}
                      onClick={() => setCurrentFeaturedBlogIndex(idx)}
                      aria-label={`Go to featured blog ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            ) : null}

            <div className="featured-blogs-view-all">
              <Link
                to="/history/blogs"
                className="button button-primary button-large featured-blogs-view-all-btn"
              >
                {i18n.language === 'ka' ? 'ყველა ბლოგის ნახვა' : 'View all blogs'}
              </Link>
            </div>
          </div>
        ) : (
          <p className="featured-blogs-empty">{i18n.language === 'ka' ? 'რჩეული ბლოგები მალე დაემატება' : 'Featured blogs coming soon'}</p>
        )}
      </section>
    </div>
  );
};

export default Home;

